/**
 * Prefetch utility — called from StoreContext immediately after the active store is
 * resolved (while loading=true). By the time the dashboard components mount and their
 * hooks fire, data is already cached in React Query → zero loading skeletons on hot
 * navigations and a perceived instant render on first visit.
 *
 * IMPORTANT: every queryKey here must exactly match the key used by the corresponding
 * hook so React Query recognises the prefetched data as a cache hit.
 */
import type { QueryClient } from '@tanstack/react-query'
import { db } from '@/lib/supabase'
import { customersService } from '@/services/customers'
import { ordersService } from '@/services/orders'
import { productsService } from '@/services/products'

const STALE_1MIN = 60 * 1000
const STALE_5MIN = 5 * 60 * 1000

/**
 * Fire three parallel prefetch requests for the dashboard's critical data.
 * Each is fire-and-forget (never awaited by the caller) and silently swallows
 * errors so a slow/failed prefetch never breaks the store loading flow.
 */
export function prefetchDashboardData(queryClient: QueryClient, storeId: string): void {
    const firstOrdersPage = { page: 1, pageSize: 10, q: '', sortKey: null, sortDir: 'desc' as const, status: 'all' as const }
    void queryClient.prefetchQuery({
        queryKey: ['orders', 'page', storeId, firstOrdersPage],
        queryFn: () => ordersService.listPage(storeId, firstOrdersPage),
        staleTime: STALE_1MIN,
    }).catch(() => { /* prefetch failure is non-critical */ })

    const firstProductsPage = { page: 1, pageSize: 10, q: '', sortKey: null, sortDir: 'desc' as const, status: 'all' as const }
    void queryClient.prefetchQuery({
        queryKey: ['products', 'page', storeId, firstProductsPage],
        queryFn: () => productsService.listPage(storeId, firstProductsPage),
        staleTime: STALE_1MIN,
    }).catch(() => { /* prefetch failure is non-critical */ })

    // Product dashboard stats — matches useDashboardProductStats queryKey.
    // Uses a count HEAD query plus recent created_at rows for the 7-day sparkline.
    void queryClient.prefetchQuery({
        queryKey: ['products-dashboard-stats', storeId],
        queryFn: async () => {
            const oldestSparklineDate = new Date()
            oldestSparklineDate.setDate(oldestSparklineDate.getDate() - 6)
            oldestSparklineDate.setHours(0, 0, 0, 0)

            const [countResult, recentResult] = await Promise.all([
                db
                    .from('products')
                    .select('id', { count: 'exact', head: true })
                    .eq('store_id', storeId),
                db
                    .from('products')
                    .select('created_at')
                    .eq('store_id', storeId)
                    .gte('created_at', oldestSparklineDate.toISOString())
                    .order('created_at', { ascending: false }),
            ])
            if (countResult.error) throw countResult.error
            if (recentResult.error) throw recentResult.error
            return {
                total: countResult.count ?? 0,
                recentCreatedAt: (recentResult.data ?? [])
                    .map((row) => row.created_at)
                    .filter((value): value is string => typeof value === 'string'),
            }
        },
        staleTime: STALE_5MIN,
    }).catch(() => { /* prefetch failure is non-critical */ })

    // Customer stats — matches useCustomerStats queryKey: ['customers', 'stats', storeId]
    void queryClient.prefetchQuery({
        queryKey: ['customers', 'stats', storeId],
        queryFn: () => customersService.getStats(storeId),
        staleTime: STALE_5MIN,
    }).catch(() => { /* prefetch failure is non-critical */ })

    // Dashboard orders (no order_items) — matches useDashboardOrders queryKey: ['orders-dashboard', storeId]
    void queryClient.prefetchQuery({
        queryKey: ['orders-dashboard', storeId],
        queryFn: async () => {
            const oldestDashboardDate = new Date()
            oldestDashboardDate.setDate(oldestDashboardDate.getDate() - 60)
            oldestDashboardDate.setHours(0, 0, 0, 0)

            const { data, error } = await db
                .from('orders')
                .select(
                    'id, order_number, store_id, customer_name, customer_phone, customer_email, ' +
                    'status, total, subtotal, shipping_cost, discount_amount, payment_method, payment_status, ' +
                    'shipping_address, shipping_city, shipping_governorate, ' +
                    'created_at, updated_at, delivered_at, confirmation_status, return_reason, source'
                )
                .eq('store_id', storeId)
                .gte('created_at', oldestDashboardDate.toISOString())
                .order('created_at', { ascending: false })
            if (error) throw error
            return data
        },
        staleTime: STALE_5MIN,
    }).catch(() => { /* prefetch failure is non-critical */ })
}
